import { createContext, Fragment, useContext, useMemo, useState, type ReactNode } from "react";
import { Link, useLocation, useMatches, useNavigate, useOutlet } from "react-router";
import { ExternalLink, Languages, LogOut, Search, Sparkles, type LucideIcon } from "lucide-react";
import { useSession } from "@pmo/api-client";
import { pickLocalized, useLocale, useTranslation } from "@pmo/i18n";
import {
  ActionScope,
  AppShell,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  ClassificationBanner,
  CommandPalette,
  CondensedPageTitle,
  ConfirmProvider,
  LanguageSwitch,
  MOD,
  PageTransition,
  Shortcut,
  SideNav,
  SideNavBrand,
  TopBar,
  cn,
  useKeyboardShortcuts,
  useSidebarCollapsed,
  useDocumentIdentity,
  type ClassificationLevel,
  type NavSection,
  type PaletteGroup,
  type Tone,
} from "@pmo/ui";
import { NotificationsBell, TenantSwitcher, UserMenu } from "./header";
import { ErrorBoundary, SessionExpiredDialog } from "./states";

export interface ShellNavItem {
  key: string;
  label: string;
  icon: LucideIcon;
  /** The colour of the item's icon tile in the side navigation. */
  tone?: Tone;
  /** In-app path, or an absolute URL when `external`. */
  to: string;
  external?: boolean;
  badge?: ReactNode;
  /** Active-state test; defaults to exact match for "/" and prefix match otherwise. */
  match?: (pathname: string) => boolean;
  /** Letter for the `g` + letter shortcut. */
  goKey?: string;
  /** Extra words for the command palette (e.g. the other language's label). */
  keywords?: string;
  /** Listed in the command palette only (e.g. pages reached through a section's own sub-navigation). */
  paletteOnly?: boolean;
}

export interface ShellNavSection {
  /** Lets the reader fold the section away; the choice is remembered. */
  key?: string;
  title?: string;
  /** Folded until the reader opens it, unless it holds the open page. */
  folded?: boolean;
  items: ShellNavItem[];
}

export interface ShellAssistantConfig {
  /** The assistant's panel content; it reads the page context (e.g. the open policy) from the route. */
  render: () => ReactNode;
  title?: string;
  /** Controls in the panel header (thread picker, new conversation). */
  actions?: ReactNode;
}

export interface ShellLayoutProps {
  /** The app's name in the browser tab (e.g. "National Policy AI"). */
  platformName: string;
  /** The line under the logo at the top of the side navigation; the workspace's name when omitted. */
  brandSubtitle?: string;
  /** The meta description for the browser, in the reader's language. */
  description?: string;
  sections: ShellNavSection[];
  /** Marking shown at the top and bottom of every screen. */
  classification?: ClassificationLevel;
  classificationCaveat?: string;
  /** Extra controls before the notifications bell. */
  headerExtra?: ReactNode;
  notifications?: boolean;
  profileHref?: string;
  loginPath?: string;
  sidebarKey: string;
  /** Extra palette groups (app actions). */
  paletteGroups?: PaletteGroup[];
  sidebarFooter?: ReactNode;
  /** The AI assistant, docked beside every page on wide screens and a side sheet on narrower ones. */
  assistant?: ShellAssistantConfig;
}

/** Route `handle` understood by the shell. */
export interface RouteHandle {
  /** The route's label in the {@link Breadcrumbs}: a label or a function of the route params. */
  crumb?: string | ((params: Record<string, string | undefined>) => string);
  /** The route's children are parts of one page: moving between them keeps the page mounted, without the page transition. */
  page?: boolean;
}

/** The page on screen: the path of the route marked `page`, if one matches. */
export function pagePath(matches: ReadonlyArray<{ handle: unknown; pathname: string }>): string | undefined {
  return matches.find((m) => (m.handle as RouteHandle | undefined)?.page)?.pathname;
}

interface ShellAssistantState {
  available: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const AssistantContext = createContext<ShellAssistantState>({ available: false, open: false, setOpen: () => {} });

/** Opens or closes the shell's docked assistant from any page (e.g. an "Ask about this" button). */
export function useShellAssistant(): ShellAssistantState {
  return useContext(AssistantContext);
}

/**
 * The route trail from each match's `handle.crumb`, as the UAE Design System's breadcrumb at the top of the page: AE Gold links,
 * underlined, the current page in semibold AE Black. The home page, whose trail is itself alone, shows none.
 */
export function Breadcrumbs({ className }: { className?: string }) {
  const { t } = useTranslation();
  const matches = useMatches();
  const crumbs = matches
    .map((m) => {
      const h = m.handle as RouteHandle | undefined;
      if (!h?.crumb) return null;
      const label = typeof h.crumb === "function" ? h.crumb(m.params) : t(h.crumb);
      return { id: m.id, label, to: m.pathname };
    })
    .filter((c): c is { id: string; label: string; to: string } => !!c);
  if (crumbs.length < 2) return null;
  return (
    <Breadcrumb data-print="hide" className={cn("min-w-0", className)}>
      <BreadcrumbList>
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          return (
            <Fragment key={c.id}>
              {i > 0 ? <BreadcrumbSeparator /> : null}
              <BreadcrumbItem>
                {last ? (
                  <BreadcrumbPage className="max-w-[24rem]">{c.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild className="max-w-[16rem]">
                    <Link to={c.to}>{c.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

const isActive = (item: ShellNavItem, pathname: string) =>
  item.external ? false : item.match ? item.match(pathname) : item.to === "/" ? pathname === "/" : pathname === item.to || pathname.startsWith(`${item.to}/`);

/**
 * The authenticated frame shared by the backoffice and the console: classification banners, the docked side navigation, the
 * header (the page title once the page's heading scrolls away, then the search field, the assistant, workspace switcher, language,
 * notifications and user menu, as the UAE Design System header's secondary navigation), the breadcrumb at the top of the page, the
 * docked assistant, the command palette (⌘K / Ctrl+K, `g` + letter), a per-page error boundary and the session-expired dialog. The
 * tenant accent comes from the session (see `SessionProvider`).
 */
export function ShellLayout({
  platformName,
  brandSubtitle,
  description,
  sections,
  classification = "internal",
  classificationCaveat,
  headerExtra,
  notifications = true,
  profileHref,
  loginPath = "/login",
  sidebarKey,
  paletteGroups = [],
  sidebarFooter,
  assistant,
}: ShellLayoutProps) {
  const { t } = useTranslation("common");
  useDocumentIdentity(platformName, description);
  const { locale, toggle, setLocale } = useLocale();
  const { tenant, logout } = useSession();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const page = pagePath(useMatches()) ?? pathname;
  const outlet = useOutlet();
  const [collapsed, toggleCollapsed] = useSidebarCollapsed(sidebarKey);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [query, setQuery] = useState("");

  const open = (item: ShellNavItem) => (item.external ? window.open(item.to, "_blank", "noopener") : navigate(item.to));

  const navItem = (item: ShellNavItem) => ({
    key: item.key,
    label: item.external ? (
      <span className="inline-flex items-center gap-1">
        {item.label}
        <ExternalLink className="size-3 opacity-60" aria-hidden />
      </span>
    ) : (
      item.label
    ),
    icon: item.icon,
    tone: item.tone,
    href: item.to,
    badge: item.badge,
    active: isActive(item, pathname),
    onClick: () => open(item),
  });
  const navSections: NavSection[] = sections
    .map((s) => ({ key: s.key, title: s.title, folded: s.folded, items: s.items.filter((item) => !item.paletteOnly).map(navItem) }))
    .filter((s) => s.items.length);

  const allItems = useMemo(() => sections.flatMap((s) => s.items), [sections]);
  const groups: PaletteGroup[] = [
    {
      id: "pages",
      heading: t("palette.pages"),
      items: allItems.map((item) => ({
        id: `page-${item.key}`,
        label: item.label,
        icon: item.icon,
        keywords: [item.keywords, item.to].filter(Boolean).join(" "),
        keys: item.goKey ? ["g", item.goKey] : undefined,
        onSelect: () => {
          setPaletteOpen(false);
          open(item);
        },
      })),
    },
    ...paletteGroups,
    {
      id: "actions",
      heading: t("palette.actions"),
      items: [
        ...(assistant
          ? [{ id: "assistant", label: t("palette.askAi"), icon: Sparkles, keywords: "ai assistant copilot مساعد ذكاء", onSelect: () => { setPaletteOpen(false); setAssistantOpen(true); } }]
          : []),
        { id: "lang", label: t("language.switchTo"), icon: Languages, keywords: "language arabic english لغة", onSelect: () => { setPaletteOpen(false); toggle(); } },
        { id: "signout", label: t("actions.signOut"), icon: LogOut, onSelect: async () => { setPaletteOpen(false); if (!(await logout())) navigate(loginPath, { replace: true }); } },
      ],
    },
  ];

  useKeyboardShortcuts({
    onPalette: () => setPaletteOpen((o) => !o),
    onSearch: () => setPaletteOpen(true),
    onHelp: () => setPaletteOpen(true),
    onGo: (letter) => {
      const item = allItems.find((i) => i.goKey === letter);
      if (item) open(item);
    },
  });

  const tenantName = tenant ? pickLocalized(locale, tenant.nameEn, tenant.nameAr) : t("app.owner");
  const brand = (folded: boolean, textClassName?: string) => <SideNavBrand subtitle={brandSubtitle ?? tenantName} collapsed={folded} onHome={() => navigate("/")} textClassName={textClassName} />;
  const assistantState = useMemo<ShellAssistantState>(() => ({ available: !!assistant, open: assistantOpen, setOpen: setAssistantOpen }), [assistant, assistantOpen]);

  return (
    <AssistantContext.Provider value={assistantState}>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:start-3 focus:top-3 focus:z-50 focus:rounded-md focus:bg-white focus:px-3 focus:py-2 focus:shadow">
        {t("app.skipToContent")}
      </a>
      <ClassificationBanner level={classification} caveat={classificationCaveat} className="sticky top-0 z-40" />
      <AppShell
        brand={brand(collapsed, "hidden sm:flex")}
        header={
          <TopBar
            end={
              <>
                <button
                  type="button"
                  onClick={() => setPaletteOpen(true)}
                  aria-label={t("palette.openLong")}
                  className="me-1 hidden h-10 w-64 items-center gap-2 rounded-lg border-2 border-aegold-600 bg-white ps-3.5 pe-2 text-sm text-aeblack-500 transition-colors duration-300 hover:border-aegold-700 hover:text-aeblack-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring @2xl/topbar:flex @5xl/topbar:w-80"
                >
                  <span className="flex-1 truncate text-start">{t("palette.open")}</span>
                  <Shortcut keys={[MOD, "K"]} />
                  <Search aria-hidden className="size-5 shrink-0 text-aegold-600" />
                </button>
                <Button variant="ghost" size="icon" className="text-aegold-600 hover:bg-white hover:text-aegold-700 @2xl/topbar:hidden" onClick={() => setPaletteOpen(true)} aria-label={t("palette.openLong")}>
                  <Search />
                </Button>
                {assistant ? (
                  <Button
                    variant="outline"
                    className={cn("gap-2 @max-xl/topbar:px-2.5", assistantOpen && "bg-aegold-100 text-aegold-900 hover:bg-aegold-100")}
                    aria-pressed={assistantOpen}
                    onClick={() => setAssistantOpen((o) => !o)}
                  >
                    <Sparkles aria-hidden />
                    <span className="hidden @xl/topbar:inline">{t("assistant.ask")}</span>
                  </Button>
                ) : null}
                <TenantSwitcher />
                {headerExtra}
                <LanguageSwitch locale={locale} onChange={setLocale} className="hidden @lg/topbar:inline-flex" />
                {notifications ? <NotificationsBell /> : null}
                <UserMenu profileHref={profileHref} loginPath={loginPath} />
              </>
            }
          >
            <CondensedPageTitle />
          </TopBar>
        }
        collapsed={collapsed}
        sidebar={<SideNav sections={navSections} collapsed={collapsed} onToggleCollapsed={toggleCollapsed} footer={collapsed ? null : sidebarFooter} />}
        mobileSidebar={
          <SideNav
            brand={brand(false)}
            sections={navSections}
            footer={
              <>
                <LanguageSwitch locale={locale} onChange={setLocale} />
                {sidebarFooter}
              </>
            }
          />
        }
        aside={
          assistant
            ? { open: assistantOpen, onOpenChange: setAssistantOpen, title: assistant.title ?? t("assistant.title"), actions: assistant.actions, children: assistantOpen ? assistant.render() : null, storageKey: `${sidebarKey}.assistant.width` }
            : undefined
        }
      >
        <Breadcrumbs className="mb-5" />
        <ConfirmProvider>
          <PageTransition routeKey={page}>
            <ActionScope emphasis="secondary">
              <ErrorBoundary resetKey={pathname}>{outlet}</ErrorBoundary>
            </ActionScope>
          </PageTransition>
        </ConfirmProvider>
      </AppShell>
      <ClassificationBanner level={classification} position="bottom" />
      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        groups={groups}
        query={query}
        onQueryChange={setQuery}
        label={t("palette.label")}
        placeholder={t("palette.placeholder")}
        emptyText={t("palette.empty")}
      />
      <SessionExpiredDialog loginPath={loginPath} />
    </AssistantContext.Provider>
  );
}
